import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  FolderPlus, Save, Trash2, CheckCircle2, Circle, Banknote, AlertTriangle,
} from 'lucide-react';
import { DOSAR_STATUS_ORDER, DOSAR_STATUS_LABELS, dosarStatusLabel } from './crmLabels';
import { toast } from 'sonner';

const STATUS_COLORS = {
  in_pregatire: 'bg-gray-100 text-gray-700',
  depus: 'bg-blue-100 text-blue-700',
  preaprobat: 'bg-indigo-100 text-indigo-700',
  evaluare: 'bg-amber-100 text-amber-700',
  aprobat_final: 'bg-emerald-100 text-emerald-700',
  contractat: 'bg-emerald-600 text-white',
  respins: 'bg-red-100 text-red-700',
};

function DosarCard({ api, dosar, onChanged, onDeleted }) {
  const [d, setD] = useState({ ...dosar });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setD({ ...dosar }); }, [dosar]);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const setObj = (obj, k, v) => setD((p) => ({ ...p, [obj]: { ...(p[obj] || {}), [k]: v } }));
  const toggleDoc = (idx) => setD((p) => {
    const docs = (p.documente || []).map((x, i) => i === idx
      ? { ...x, colectat: !x.colectat, data: !x.colectat ? new Date().toISOString().slice(0, 10) : null }
      : x);
    return { ...p, documente: docs };
  });

  const payload = () => ({
    banca: d.banca, status: d.status, documente: d.documente,
    preaprobare: {
      data: d.preaprobare?.data || null,
      valabilitateZile: d.preaprobare?.valabilitateZile ? Number(d.preaprobare.valabilitateZile) : null,
      suma: d.preaprobare?.suma ? Number(d.preaprobare.suma) : null,
    },
    evaluare: {
      status: d.evaluare?.status || null,
      valoare: d.evaluare?.valoare ? Number(d.evaluare.valoare) : null,
      data: d.evaluare?.data || null,
    },
    decizie: {
      status: d.decizie?.status || null,
      data: d.decizie?.data || null,
      observatii: d.decizie?.observatii || null,
    },
    valoareCredit: d.valoareCredit ? Number(d.valoareCredit) : null,
    dobanda: d.dobanda ? Number(d.dobanda) : null,
    perioadaLuni: d.perioadaLuni ? Number(d.perioadaLuni) : null,
    perioadaFixaLuni: d.perioadaFixaLuni ? Number(d.perioadaFixaLuni) : null,
    moneda: d.moneda || 'RON',
    observatii: d.observatii || null,
  });

  const save = async (overrideStatus) => {
    setSaving(true);
    try {
      const body = payload();
      if (overrideStatus) body.status = overrideStatus;
      await api.put(`/dosare/${d.id}`, body);
      toast.success(overrideStatus === 'contractat' ? 'Dosar disbursat — client avansat în Retenție' : 'Dosar salvat');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la salvare dosar');
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    onDeleted && onDeleted();
  };

  const docsDone = (d.documente || []).filter((x) => x.colectat).length;
  const docsTotal = (d.documente || []).length;
  const complet = docsTotal > 0 && docsDone === docsTotal;
  const expira = d.preaprobare?.expira;
  const expiraSoon = expira && expira <= new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);

  return (
    <Card className="border-gray-200" data-testid={`crm-dosar-${d.id}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <Banknote className="h-4 w-4 text-blue-600" />
          <CardTitle className="text-sm">{d.banca || 'Bancă'}</CardTitle>
          <Badge className={STATUS_COLORS[d.status] || 'bg-gray-100'}>{dosarStatusLabel(d.status)}</Badge>
          <Badge variant="outline" className={complet ? 'text-emerald-700 border-emerald-300' : 'text-amber-700 border-amber-300'}>
            {complet ? 'Complet' : `Incomplet ${docsDone}/${docsTotal}`}
          </Badge>
          {expiraSoon && (
            <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Preaprobare expiră {expira}
            </Badge>
          )}
          <Button size="icon" variant="ghost" className="ml-auto h-7 w-7 text-red-500"
            onClick={del} data-testid={`crm-dosar-del-${d.id}`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {/* Status */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Status dosar</Label>
            <select className="w-full border rounded-md h-9 px-2 text-sm" value={d.status}
              onChange={(e) => set('status', e.target.value)} data-testid={`crm-dosar-status-${d.id}`}>
              {DOSAR_STATUS_ORDER.map((s) => <option key={s} value={s}>{DOSAR_STATUS_LABELS[s]}</option>)}
            </select>
          </div>
        </div>

        {/* Checklist documente */}
        <div>
          <Label className="text-xs">Documente necesare</Label>
          {docsTotal === 0 && <p className="text-xs text-gray-400 mt-1">Adaugă tipuri de documente în Setări CRM.</p>}
          <div className="flex flex-wrap gap-2 mt-1">
            {(d.documente || []).map((doc, idx) => (
              <button key={doc.nume + idx} onClick={() => toggleDoc(idx)}
                className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border ${doc.colectat ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}
                data-testid={`crm-dosar-doc-${idx}`}>
                {doc.colectat ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />} {doc.nume}
              </button>
            ))}
          </div>
        </div>

        {/* Preaprobare */}
        <div className="border-t pt-2">
          <Label className="text-xs font-semibold">Preaprobare</Label>
          <div className="grid grid-cols-3 gap-3 mt-1">
            <div>
              <Label className="text-[11px] text-gray-500">Data</Label>
              <Input type="date" value={d.preaprobare?.data || ''}
                onChange={(e) => setObj('preaprobare', 'data', e.target.value)} data-testid={`crm-dosar-prea-data-${d.id}`} />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Valabilitate (zile)</Label>
              <Input type="number" value={d.preaprobare?.valabilitateZile || ''}
                onChange={(e) => setObj('preaprobare', 'valabilitateZile', e.target.value)} placeholder="30" />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Sumă preaprobată</Label>
              <Input type="number" value={d.preaprobare?.suma || ''}
                onChange={(e) => setObj('preaprobare', 'suma', e.target.value)} />
            </div>
          </div>
          {expira && <div className="text-xs text-gray-600 mt-1">Expiră la: <strong>{expira}</strong></div>}
        </div>

        {/* Evaluare */}
        <div className="border-t pt-2">
          <Label className="text-xs font-semibold">Evaluare imobil</Label>
          <div className="grid grid-cols-3 gap-3 mt-1">
            <div>
              <Label className="text-[11px] text-gray-500">Status</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={d.evaluare?.status || ''}
                onChange={(e) => setObj('evaluare', 'status', e.target.value)}>
                <option value="">—</option>
                <option value="comandata">Comandată</option>
                <option value="in_lucru">În lucru</option>
                <option value="finalizata">Finalizată</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Valoare evaluată</Label>
              <Input type="number" value={d.evaluare?.valoare || ''}
                onChange={(e) => setObj('evaluare', 'valoare', e.target.value)} />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Data</Label>
              <Input type="date" value={d.evaluare?.data || ''}
                onChange={(e) => setObj('evaluare', 'data', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Decizie finală */}
        <div className="border-t pt-2">
          <Label className="text-xs font-semibold">Decizie finală</Label>
          <div className="grid grid-cols-3 gap-3 mt-1">
            <div>
              <Label className="text-[11px] text-gray-500">Status</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={d.decizie?.status || ''}
                onChange={(e) => setObj('decizie', 'status', e.target.value)}>
                <option value="">—</option>
                <option value="aprobat">Aprobat</option>
                <option value="respins">Respins</option>
                <option value="in_analiza">În analiză</option>
              </select>
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Data</Label>
              <Input type="date" value={d.decizie?.data || ''}
                onChange={(e) => setObj('decizie', 'data', e.target.value)} />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Observații</Label>
              <Input value={d.decizie?.observatii || ''}
                onChange={(e) => setObj('decizie', 'observatii', e.target.value)} />
            </div>
          </div>
        </div>

        {/* Credit (disbursare) */}
        <div className="border-t pt-2">
          <Label className="text-xs font-semibold">Credit (la disbursare)</Label>
          <div className="grid grid-cols-5 gap-3 mt-1">
            <div>
              <Label className="text-[11px] text-gray-500">Valoare</Label>
              <Input type="number" value={d.valoareCredit || ''}
                onChange={(e) => set('valoareCredit', e.target.value)} />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Dobândă %</Label>
              <Input type="number" step="0.01" value={d.dobanda || ''}
                onChange={(e) => set('dobanda', e.target.value)} />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Perioadă (luni)</Label>
              <Input type="number" value={d.perioadaLuni || ''}
                onChange={(e) => set('perioadaLuni', e.target.value)} />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Dob. fixă (luni)</Label>
              <Input type="number" value={d.perioadaFixaLuni || ''}
                onChange={(e) => set('perioadaFixaLuni', e.target.value)}
                placeholder="36" data-testid={`crm-dosar-fix-${d.id}`} />
            </div>
            <div>
              <Label className="text-[11px] text-gray-500">Monedă</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={d.moneda || 'RON'}
                onChange={(e) => set('moneda', e.target.value)}>
                <option value="RON">RON</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={() => save()} disabled={saving} data-testid={`crm-dosar-save-${d.id}`}>
            <Save className="h-4 w-4 mr-1" /> {saving ? 'Se salvează…' : 'Salvează'}
          </Button>
          {d.status !== 'contractat' && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={saving}
              onClick={() => save('contractat')} data-testid={`crm-dosar-disburse-${d.id}`}>
              <CheckCircle2 className="h-4 w-4 mr-1" /> Marchează Disbursat
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function DosarePanel({ api, solicitareId, banci = [], canAdd = true, onChanged }) {
  const [dosare, setDosare] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [banca, setBanca] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/solicitari/${solicitareId}/dosare`);
      setDosare(r.dosare || []);
    } catch (e) { /* noop */ } finally { setLoading(false); }
  }, [api, solicitareId]);

  useEffect(() => { load(); }, [load]);

  const addDosar = async () => {
    if (!banca) { toast.error('Alege banca'); return; }
    try {
      await api.post(`/solicitari/${solicitareId}/dosare`, { banca });
      toast.success('Dosar creat');
      setShowAdd(false); setBanca('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la creare dosar');
    }
  };

  const delDosar = async (id) => {
    try { await api.del(`/dosare/${id}`); toast.success('Dosar șters'); load(); }
    catch (e) { toast.error('Eroare la ștergere'); }
  };

  return (
    <Card data-testid="crm-dosare-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Dosare la bancă ({dosare.length})</CardTitle>
        {canAdd && (
          <Button size="sm" onClick={() => setShowAdd(true)} data-testid="crm-dosar-add">
            <FolderPlus className="h-4 w-4 mr-1" /> Adaugă dosar
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && <p className="text-sm text-gray-500">Se încarcă…</p>}
        {!loading && dosare.length === 0 && (
          <p className="text-sm text-gray-400">Niciun dosar încă. Adaugă un dosar pentru fiecare bancă la care depui cererea.</p>
        )}
        {dosare.map((dos) => (
          <DosarCard key={dos.id} api={api} dosar={dos}
            onChanged={() => { load(); onChanged && onChanged(); }}
            onDeleted={() => delDosar(dos.id)} />
        ))}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={(v) => !v && setShowAdd(false)}>
        <DialogContent data-testid="crm-dosar-add-dialog">
          <DialogHeader><DialogTitle>Adaugă dosar la bancă</DialogTitle></DialogHeader>
          <div>
            <Label className="text-xs">Banca</Label>
            <select className="w-full border rounded-md h-10 px-2 text-sm" value={banca}
              onChange={(e) => setBanca(e.target.value)} data-testid="crm-dosar-add-banca">
              <option value="">Alege banca…</option>
              {banci.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Anulează</Button>
            <Button onClick={addDosar} data-testid="crm-dosar-add-submit">Adaugă</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
